import type { Currency } from '@shared/utils';
import type { BundleStatus } from '../../Offer/types';
import type { OfferBundlePromotion, ProductVerticalCategory, PromotionAmount } from '../../types';

type PromoType = 'offers' | 'bonuses' | 'free-spins';
type PromoStatus = 'active' | 'completed' | 'expired' | 'pending';
type FreeSpinsStatus = 'ACTIVE' | 'USED' | 'EXPIRED' | 'PENDING';
type PromotionsPageType = PromoType | 'free-bets';

type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
  trackingProps?: Record<string, unknown>;
  filteredVertical?: string[];
};

type PromotionsToolbarProps = {
  title: string;
  className?: string;
  isPrimaryPage?: boolean;
  children?: React.ReactNode;
};

type OfferBundleAssignation = {
  id: string;
  activatedDate: string;
  validTo: string;
  bundleCode: string;
  colour: string;
  description: string;
  image: {
    link: string;
    altText: string;
  };
  termsAndConditions: string;
  title: string;
  status: BundleStatus;
  iterationNr: number;
  isActive: boolean;
  showExpiryTag: boolean;
  lockedByInvalidAccount: boolean;
  deferred: boolean;
};

type BonusFundMoney = {
  amount: number;
  fixed_amount: number;
  fixed_amount_scale: number;
  currency: Currency;
};

type BonusFundStatus = 'PENDING' | 'ACTIVE' | 'DEACTIVATED' | 'EXPIRED' | 'CONSUMED' | 'REDEEMED';

type BonusFundNormalOffer = {
  id: string;
  amount: BonusFundMoney;
  reason: string;
  activationDate?: string;
  expireDate: string;
  bonusProductTypes?: string[];
  productIds: string[];
  gameIds?: string[];
  offerCode: string;
  iterationNr: number;
  bundleCode: string;
  status: BonusFundStatus;
  currentAmount: BonusFundMoney;
  wallet: boolean;
  singleOffer: boolean;
  depositPendingToBeReturn: boolean;
  forBingoGame: boolean;
  pageMappingId: string;
  ctaStaticKey: string;
  image?: {
    link: string;
    altText?: string;
  };
};

type WagerBonusFundOffer = BonusFundNormalOffer & {
  wageringRequired: BonusFundMoney;
  currentWageredAmount: BonusFundMoney;
  maxBet?: BonusFundMoney;
  maxWinnings?: BonusFundMoney;
};

type BonusOffer = BonusFundNormalOffer | WagerBonusFundOffer;

type BonusOfferData = {
  total: number;
  list: BonusOffer[];
};

function isWagerBonusFundOffer(item: BonusOffer): item is WagerBonusFundOffer {
  return 'wageringRequired' in item;
}

type FreeSpinsOffer = {
  id: string;
  endDate: string;
  gameName: string;
  freeSpins: number; // Current remaining spins
  status: FreeSpinsStatus;
  wins?: PromotionAmount;
  initialFreeSpins: number; // Total awarded spins
};

type FreeSpinsOfferData = {
  total: number;
  list: FreeSpinsOffer[];
};

export type {
  BonusFundMoney,
  BonusFundNormalOffer,
  BonusFundStatus,
  BonusOffer,
  BonusOfferData,
  FreeSpinsOffer,
  FreeSpinsOfferData,
  FreeSpinsStatus,
  OfferBundleAssignation,
  OfferBundlePromotion,
  Option,
  ProductVerticalCategory,
  PromoStatus,
  PromotionsPageType,
  PromotionsToolbarProps,
  PromoType,
  WagerBonusFundOffer,
};

export { isWagerBonusFundOffer };
